import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { TextareaAutosize, FormHelperText, Box, Switch } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Globe, Mail as MailIcon, Type } from 'lucide-mui';
import LanguageSelect from '../library/files/FilesEmpty/LanguageSelect';
import { updateTeamSettingsThunk } from '../team/teamSlice';
import { RootState } from '../../reducers';
import logger from '../../lib/logger';
import useLimits from '../plan/hooks/limits';
import { ToggleSetting, SettingsSection } from './common';
import { updateSettings } from './settingsSlice';

const AISettings: React.FC = () => {
  const dispatch = useDispatch();
  const { team } = useSelector((state: RootState) => state);
  const { selectedTeam } = team;
  const customVocabulary = selectedTeam?.fileOptions?.customVocabulary || '';
  const [value, setValue] = useState(customVocabulary);
  const [error, setError] = useState('');
  const guardLimits = useLimits();
  const theme = useTheme();

  const recorder = useSelector((state: RootState) => state.settings.recorder);
  const emailAlerts = recorder.otherSettings?.emailAlerts || false;
  const [transcriptionEmailAlerts, setTranscriptionEmailAlerts] = useState(
    emailAlerts
  );

  useEffect(() => {
    setTranscriptionEmailAlerts(emailAlerts);
  }, [emailAlerts]);

  useEffect(() => {
    setValue(customVocabulary);
  }, [customVocabulary]);

  const handleEmailAlertsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newEmailAlertsValue = event.target.checked;
    setTranscriptionEmailAlerts(newEmailAlertsValue);
    console.log('Transcription email alerts:', newEmailAlertsValue);

    dispatch(
      updateSettings({
        key: 'recorder',
        newSettings: {
          ...recorder,
          otherSettings: {
            ...recorder.otherSettings,
            emailAlerts: newEmailAlertsValue,
          },
        },
      })
    );
  };

  const debouncedUpdate = useCallback(
    debounce(async (newValue: string) => {
      try {
        await guardLimits('customVocabulary', async () => {
          await dispatch(
            updateTeamSettingsThunk({
              teamId: selectedTeam?.teamId || '',
              settings: {
                fileOptions: {
                  customVocabulary: newValue,
                },
              },
            })
          );
        });
      } catch (err) {
        logger.error('Error updating custom vocabulary:', err);
        setError('Failed to update custom vocabulary');
      }
    }, 1000),
    [selectedTeam?.teamId]
  );

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    setError('');
    debouncedUpdate(newValue);
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        bgcolor: 'transparent',
        px: 3,
        pt: 1,
        pb: 3,
        '& > *:not(:last-child)': {
          mb: 3,
        },
      }}
    >
      <SettingsSection title="Language">
        <ToggleSetting
          checked={false}
          onChange={() => {}}
          label="Transcription Language"
          description="Choose the language you want the AI to use for transcription. If you use multiple languages, select 'Auto' and we'll detect it for you."
          icon={<Globe sx={{ fontSize: 20 }} />}
          rightElement={
            <Box
              sx={{
                width: { xs: '100%', sm: 'auto' },
                mt: { xs: 2, sm: 0 },
                display: 'flex',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >
              <LanguageSelect noCollapse />
            </Box>
          }
        />
        <ToggleSetting
          checked={transcriptionEmailAlerts}
          onChange={handleEmailAlertsChange}
          label="Email Alerts"
          description="Enable or disable receiving email alerts after transcription is completed."
          icon={<MailIcon />}
          rightElement={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 2,
              }}
            >
              <Switch
                checked={transcriptionEmailAlerts}
                onChange={handleEmailAlertsChange}
                color="primary"
              />
            </Box>
          }
        />
      </SettingsSection>

      <SettingsSection title="Vocabulary">
        <ToggleSetting
          checked={false}
          onChange={() => {}}
          label="Custom Vocabulary"
          description="Add specialized terms, acronyms, or names to improve transcription accuracy"
          icon={<Type sx={{ fontSize: 20 }} />}
        >
          <Box sx={{ width: '100%', mt: { xs: 2, sm: 0 } }}>
            <TextareaAutosize
              value={value}
              onChange={handleChange}
              placeholder="Enter custom terms, one per line"
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '8px',
                borderRadius: '4px',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: 'transparent',
                color: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.body2.fontSize,
                resize: 'vertical',
              }}
            />
            {error && (
              <FormHelperText error sx={{ mt: 1 }}>
                {error}
              </FormHelperText>
            )}
          </Box>
        </ToggleSetting>
      </SettingsSection>
    </Box>
  );
};

export default AISettings;
